import React, { useState, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Typography,
    Box,
    useMediaQuery,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const StyledTableCell = TableCell;
const StyledTableRow = TableRow;

const ClientTable = React.memo(({
    data,
    isAdmin,
    handleDelete,
    filterConfig,
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const filteredClients = useMemo(() => {
        if (!data || data.length === 0) return [];
        
        let result = [...data];
        if (filterConfig?.city) {
            result = result.filter((m) =>
                m.city?.toLowerCase().includes(filterConfig.city.toLowerCase())
            );
        }
        if (filterConfig?.schoolName) {
            result = result.filter((m) =>
                m.schoolName?.toLowerCase().includes(filterConfig.schoolName.toLowerCase())
            );
        }

        // Alphabetically sort school names
        return result.sort((a, b) =>
            a.schoolName?.toLowerCase().localeCompare(b.schoolName?.toLowerCase())
        );
    }, [data, filterConfig]);

    const paginatedClients = useMemo(() => {
        return filteredClients.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [filteredClients, page, rowsPerPage]);

    return (
        <Paper
            sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: 3,
                overflow: "hidden",
                mb: 4, // Ensure spacing from the bottom
            }}
        >
            <Box sx={{ overflowX: "auto" }}>
                <TableContainer sx={{ minWidth: 600 }}>
                    <Table aria-label="client table">
                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableRow>
                                <StyledTableCell><strong>No.</strong></StyledTableCell>
                                <StyledTableCell><strong>School Name</strong></StyledTableCell>
                                <StyledTableCell><strong>Email</strong></StyledTableCell>
                                <StyledTableCell><strong>City</strong></StyledTableCell>
                                <StyledTableCell align="right"><strong>Actions</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedClients.length > 0 ? (
                                paginatedClients.map(({ id, schoolName, email, city }, index) => (
                                    <StyledTableRow
                                        key={id}
                                        sx={{
                                            "&:hover": { backgroundColor: "#fafafa" },
                                            transition: "all 0.2s",
                                        }}
                                    >
                                        <StyledTableCell>
                                            {page * rowsPerPage + index + 1}
                                        </StyledTableCell>
                                        <StyledTableCell>{schoolName}</StyledTableCell>
                                        <StyledTableCell>{email}</StyledTableCell>
                                        <StyledTableCell>{city}</StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                                <Tooltip title="View Profile">
                                                    <IconButton
                                                        component={Link}
                                                        to={`/view/client/${id}`}
                                                        color="primary"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {isAdmin && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            onClick={(e) => handleDelete(e, id)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No Clients Found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Pagination Fixed */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "flex-end",
                    mt: 2,
                    px: isMobile ? 0 : 2,
                }}
            >
                <TablePagination
                    component="div"
                    count={filteredClients.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    showFirstButton
                    showLastButton
                    sx={{
                        width: "100%",
                        ".MuiTablePagination-toolbar": {
                            justifyContent: isMobile ? "center" : "flex-end",
                            flexWrap: "wrap",
                        },
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: "0 8px",
                        },
                    }}
                />
            </Box>
        </Paper>
    );
});

export default ClientTable;
